/**
 * Portal Layout Component
 *
 * Story: 11-1-portal-de-acesso-externo
 * Task: Frontend - PortalLayout Component
 *
 * Simplified layout for external partner portal
 * - Minimal sidebar with key navigation items
 * - Header with company branding and logout
 * - Mobile-responsive with collapsible sidebar
 *
 * D3 (passo 19): monta o ao vivo do portal e mostra o selo de respostas não lidas no Atendimento.
 */
import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ReinscricaoDoPush } from "@/components/notifications/ReinscricaoDoPush";
import { AvisoDaUrlDoPortal } from "@/components/portal/AvisoDaUrlDoPortal";
import { ContadorDoAtendimento } from "@/components/portal/ContadorDoAtendimento";
import { PortalAoVivo } from "@/components/portal/PortalAoVivo";
import { Button } from "@/components/ui/button";
// Aviso de atualização do PWA — ver o bloco explicativo no JSX.
import { UpdatePrompt } from "@/components/pwa/UpdatePrompt";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  LayoutDashboard,
  FileText,
  FileImage,
  FolderOpen,
  BookOpen,
  FileOutput,
  DollarSign,
  Home,
  LogOut,
  Menu,
  MessageSquare,
  Settings,
  SlidersHorizontal,
  Users,
  X,
  Building2,
} from "lucide-react";
import { usePortalAuth } from "@/contexts/PortalAuthContext";
import { useNaoLidasDoAtendimento } from "@/hooks/useNaoLidasDoAtendimento";
import { usePortalPermissions } from "@/hooks/usePortalPermissions";
import { PortalNotificationBell } from "@/components/portal/PortalNotificationBell";
import { NotificationPrompt } from "@/components/pwa/NotificationPrompt";
import { PUSH_BASE_PORTAL } from "@/hooks/usePushSubscription";
import { dashboardTemConteudo } from "@/lib/portal/dashboardTemConteudo";
import { textoDasNaoLidas } from "@/lib/portal/naoLidasDoAtendimento";
import { cn } from "@/lib/utils";

// ============================================================================
// Types
// ============================================================================

interface PortalLayoutProps {
  children: React.ReactNode;
}

interface NavItem {
  name: string;
  href: string;
  icon: React.ElementType;
  /**
   * Optional permission required to see this nav item.
   * If set, item is only rendered when `usePortalPermissions().has(requiredPerm)` returns true.
   */
  requiredPerm?: string;
  /**
   * PR 5 — alternativa a `requiredPerm` para quando "aparece no menu" não é
   * uma permissão só, mas uma REGRA (ex.: Dashboard só com conteúdo). Recebe
   * o `has` de `usePortalPermissions()` e decide sozinho.
   */
  visivel?: (has: (perm: string) => boolean) => boolean;
}

// ============================================================================
// Navigation Items
// ============================================================================

// Exportado para o teste conseguir afirmar sobre os OBJETOS, não sobre o texto
// do arquivo. Um item sem `requiredPerm`/`visivel` aparece no menu mesmo quando
// a API nega — foi o defeito que só o navegador pegou no C1. "Início" é a
// única exceção deliberada: não é tela desligável, todo parceiro autenticado
// a vê.
export const navItems: NavItem[] = [
  {
    name: "Início",
    href: "/portal/inicio",
    icon: Home,
  },
  {
    name: "Dashboard",
    href: "/portal/dashboard",
    icon: LayoutDashboard,
    visivel: dashboardTemConteudo,
  },
  {
    name: "Comprovantes",
    href: "/portal/comprovantes",
    icon: FileImage,
    requiredPerm: "portal:comprovantes:view",
  },
  {
    name: "Processos",
    href: "/portal/processos",
    icon: FileText,
    requiredPerm: "portal:processos:view",
  },
  {
    name: "Documentos",
    href: "/portal/documentos",
    icon: FolderOpen,
    requiredPerm: "portal:documentos:view",
  },
  {
    name: "Base de Conhecimento",
    href: "/portal/conhecimento",
    icon: BookOpen,
    requiredPerm: "portal:conhecimento:view",
  },
  {
    // Pacote D — arquivos que o DONO compartilha com a equipe dele. Distinto de
    // "Documentos", que é o que o SE_WEB publica para o parceiro.
    name: "Documentos da Equipe",
    href: "/portal/documentos-equipe",
    icon: Users,
    requiredPerm: "portal:documentos_equipe:view",
  },
  {
    // Pacote C2 — o dono escolhe o que a equipe dele vê. Só aparece para quem
    // tem `portal:equipe:manage`, que o funcionário não recebe.
    name: "Telas da Equipe",
    href: "/portal/telas-equipe",
    icon: SlidersHorizontal,
    requiredPerm: "portal:equipe:manage",
  },
  {
    name: "Gerar Remessa",
    href: "/portal/remessas",
    icon: FileOutput,
    requiredPerm: "portal:remessas:view",
  },
  {
    name: "Atendimento",
    href: "/portal/atendimento",
    icon: MessageSquare,
    requiredPerm: "portal:atendimento:view",
  },
  {
    name: "Financeiro",
    href: "/portal/financeiro",
    icon: DollarSign,
    requiredPerm: "portal:financeiro:view",
  },
];

const HREF_DO_ATENDIMENTO = "/portal/atendimento";

/** O nome do item; no "Atendimento", o selo de respostas não lidas à direita (peça I do desenho do portal). O nome trunca para o selo nunca empurrá-lo. */
function RotuloDoItem({ nome, href, naoLidas }: { nome: string; href: string; naoLidas: number }) {
  if (href !== HREF_DO_ATENDIMENTO) return <>{nome}</>;
  return (
    <>
      <span className="min-w-0 flex-1 truncate">{nome}</span>
      {/* A ÚNICA região viva do selo: o leitor de tela ouve uma vez a cada mudança (no botão "Menu" e na lista o selo é aria-hidden).
          🔴 SEMPRE montada (vazia com 0) e com o texto DENTRO dela: NVDA/JAWS costumam não anunciar uma região que já nasce preenchida, e a
          passagem 0→1 — a que mais importa, e a única pista no nível "Só no contador" — ficaria muda. O número visual é irmão, aria-hidden. */}
      <span role="status" className="sr-only">
        {naoLidas > 0 && `${textoDasNaoLidas(naoLidas)} no atendimento`}
      </span>
      <ContadorDoAtendimento total={naoLidas} aria-hidden="true" className="ml-auto" />
    </>
  );
}

// ============================================================================
// Component
// ============================================================================

const PortalLayout: React.FC<PortalLayoutProps> = ({ children }) => {
  const { user, logout } = usePortalAuth();
  const { has } = usePortalPermissions();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // D3 (passo 19): respostas do atendente não lidas (0 sem a permissão do Atendimento). Vem do cache que o `PortalAoVivo` mantém ao vivo.
  const { total: naoLidasDoAtendimento } = useNaoLidasDoAtendimento();
  const rotuloDoBotaoMenu = sidebarOpen
    ? "Fechar menu"
    : naoLidasDoAtendimento > 0
      ? `Abrir menu (${textoDasNaoLidas(naoLidasDoAtendimento)} no atendimento)`
      : "Abrir menu";

  // Filter nav items by RBAC permissions (hide items user cannot access) —
  // `visivel` (regra própria, ex.: Dashboard) tem prioridade sobre `requiredPerm`.
  const visibleNavItems = navItems.filter((item) => {
    if (item.visivel) return item.visivel(has);
    return !item.requiredPerm || has(item.requiredPerm);
  });

  // Get user initials for avatar
  const getUserInitials = (): string => {
    if (!user) return "PA";
    const firstInitial = user.first_name?.charAt(0) || "";
    const lastInitial = user.last_name?.charAt(0) || "";
    return (firstInitial + lastInitial).toUpperCase() || "PA";
  };

  // Handle logout with confirmation
  const handleLogout = () => {
    if (window.confirm("Tem certeza que deseja sair?")) {
      logout();
    }
  };

  // Close sidebar on mobile when route changes
  React.useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  return (
    // `h-screen` é `height: 100vh` — e no celular `100vh` é a altura do LAYOUT
    // viewport, que inclui a faixa atrás da barra de URL do navegador. O portal
    // inteiro ficava mais alto que a área visível, e tudo no rodapé (campo de
    // mensagem do chat, botões, fim da página) era empurrado para baixo da
    // dobra — o Augusto reportou como "cortado" em VÁRIAS telas do portal
    // (comprovante, atendimento, chat), o que é a assinatura de um problema no
    // contêiner que envolve todas elas, não em cada tela.
    //
    // `h-dvh` (dynamic viewport height, Tailwind 3.4+) acompanha a barra do
    // navegador: a altura passa a ser a VISÍVEL.
    <div className="h-dvh flex flex-col bg-gradient-to-br from-emerald-50/40 via-white to-white">
      {/* AVISO DE ATUALIZAÇÃO — o defeito que escondeu TODOS os consertos.

          O `<UpdatePrompt />` só existia dentro do `<ProtectedRoute>`, a árvore
          de rotas do app INTERNO. O portal usa outra árvore
          (`ProtectedPortalRoute` → `PortalLayout`), então o parceiro NUNCA via
          o aviso.

          Com `registerType: "prompt"` (vite.config.ts), o Service Worker novo
          instala e fica em `waiting` até alguém aceitar. Sem o aviso ninguém
          aceita: o parceiro fica preso no bundle antigo INDEFINIDAMENTE, por
          mais deploys que se faça.

          Medido em produção (06/08/2026, com conta de parceiro): a página servia
          `index.DyK3OIsX.js` — chunk que já devolvia 404 no servidor, vindo
          inteiro do cache do SW — enquanto o `index.html` real apontava para
          outro. O registro tinha `waiting: true` e a raiz ainda usava
          `h-screen`, corrigido no dia anterior.

          É por isso que o Augusto dizia "é como se não tivesse feito nada":
          era literalmente verdade do lado dele. E não apareceu nas minhas
          verificações porque `curl` NÃO passa pelo Service Worker. */}
      <UpdatePrompt />

      {/* D3 (passo 19): o ao vivo do portal (socket, presença, aviso na tela, som), o consumidor do `?aviso=<id>` do push (sem ele o clique abre
          a página e a linha segue não lida) e a reinscrição do push — as mesmas peças da árvore interna (`App.tsx:449-451`), com a API do portal. */}
      <PortalAoVivo />
      <AvisoDaUrlDoPortal />
      <ReinscricaoDoPush basePath={PUSH_BASE_PORTAL} />

      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-emerald-100 z-40 flex-shrink-0">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Left: Mobile menu button + Logo */}
            <div className="flex items-center space-x-3">
              {/* Botão do menu no mobile.
                  Reportado pelo Augusto: "fica pequenininho demais, não fica
                  intuitivo". Era `size="sm"` na variante `ghost` — sem fundo,
                  ícone de 20px, baixo contraste. Agora tem altura de 48px,
                  ícone de 24px, fundo sólido e o rótulo "Menu": ícone sozinho
                  não diz o que faz. 48px cabe no header de 64px (`h-16`). */}
              <Button
                variant="secondary"
                className="relative lg:hidden h-12 gap-2 px-3"
                onClick={() => setSidebarOpen(!sidebarOpen)}
                aria-label={rotuloDoBotaoMenu}
                aria-expanded={sidebarOpen}
                data-testid="portal-menu-mobile"
              >
                {sidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                <span className="text-sm font-medium">Menu</span>
                {/* Com o menu aberto o selo some: o botão vira "Fechar menu" e o número já está no item, logo abaixo. */}
                {!sidebarOpen && (
                  <ContadorDoAtendimento
                    total={naoLidasDoAtendimento}
                    aria-hidden="true"
                    className="absolute -right-1.5 -top-1.5 ring-2 ring-white"
                    data-testid="portal-menu-mobile-selo"
                  />
                )}
              </Button>

              {/* Logo and company name */}
              <div className="flex items-center space-x-3">
                <img
                  src="/ponto-banese-logo.png"
                  alt="Ponto Banese"
                  className="h-10 w-10 object-contain drop-shadow-sm"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = "none";
                    const fallback = (e.target as HTMLImageElement).nextElementSibling;
                    if (fallback) (fallback as HTMLElement).style.display = "flex";
                  }}
                />
                <div className="h-10 w-10 rounded-md bg-gradient-to-br from-emerald-500 to-emerald-700 hidden items-center justify-center shadow-sm">
                  <Building2 className="h-5 w-5 text-white" />
                </div>
                <div className="hidden sm:block">
                  <p className="text-sm font-semibold text-emerald-900">Portal do Parceiro</p>
                  {user?.substabelecido?.razao_social && (
                    <p className="text-xs text-slate-500 truncate max-w-xs">
                      {user.substabelecido.razao_social}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Right: Notifications + User menu */}
            <div className="flex items-center gap-1">
              <PortalNotificationBell />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="relative h-10 w-10 rounded-full ring-2 ring-emerald-100 hover:ring-emerald-200 transition-all"
                  >
                    <Avatar className="h-10 w-10">
                      <AvatarFallback className="bg-gradient-to-br from-emerald-500 to-emerald-700 text-white text-sm font-semibold">
                        {getUserInitials()}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56" align="end" forceMount>
                  <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium leading-none">
                        {user?.first_name} {user?.last_name}
                      </p>
                      <p className="text-xs leading-none text-muted-foreground truncate">
                        {user?.email}
                      </p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/portal/preferencias">
                      <Settings className="mr-2 h-4 w-4" />
                      <span>Preferências</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>Sair</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      <div className="flex flex-1 min-h-0">
        {/* Sidebar - Desktop */}
        <aside className="hidden lg:flex lg:flex-shrink-0">
          <div className="flex flex-col w-64">
            <div className="flex flex-col flex-grow bg-white/70 backdrop-blur-sm border-r border-emerald-100/60 pt-6 pb-4 overflow-y-auto">
              <nav className="flex-1 px-3 space-y-1">
                {visibleNavItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.href;

                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={cn(
                        "group flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-all",
                        isActive
                          ? "bg-gradient-to-r from-emerald-50 to-emerald-100/50 text-emerald-800 shadow-sm border border-emerald-200/60"
                          : "text-slate-700 hover:bg-emerald-50/60 hover:text-emerald-800"
                      )}
                    >
                      <Icon
                        className={cn(
                          "mr-3 flex-shrink-0 h-5 w-5",
                          isActive
                            ? "text-emerald-700"
                            : "text-slate-400 group-hover:text-emerald-600"
                        )}
                      />
                      <RotuloDoItem
                        nome={item.name}
                        href={item.href}
                        naoLidas={naoLidasDoAtendimento}
                      />
                    </Link>
                  );
                })}
              </nav>
            </div>
          </div>
        </aside>

        {/* Sidebar - Mobile */}
        {sidebarOpen && (
          <div className="lg:hidden fixed inset-0 z-40 flex">
            {/* Overlay */}
            <div
              className="fixed inset-0 bg-gray-600 bg-opacity-75"
              onClick={() => setSidebarOpen(false)}
            />

            {/* Sidebar panel */}
            <div className="relative flex-1 flex flex-col max-w-xs w-full bg-white">
              <div className="absolute top-0 right-0 -mr-12 pt-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="ml-1 text-white"
                  onClick={() => setSidebarOpen(false)}
                >
                  <X className="h-6 w-6" />
                </Button>
              </div>

              <div className="flex-1 h-0 pt-5 pb-4 overflow-y-auto">
                <nav className="mt-5 px-3 space-y-1">
                  {visibleNavItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = location.pathname === item.href;

                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        className={cn(
                          "group flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-all",
                          isActive
                            ? "bg-gradient-to-r from-emerald-50 to-emerald-100/50 text-emerald-800 shadow-sm border border-emerald-200/60"
                            : "text-slate-700 hover:bg-emerald-50/60 hover:text-emerald-800"
                        )}
                      >
                        <Icon
                          className={cn(
                            "mr-3 flex-shrink-0 h-5 w-5",
                            isActive
                              ? "text-emerald-700"
                              : "text-slate-400 group-hover:text-emerald-600"
                          )}
                        />
                        <RotuloDoItem
                          nome={item.name}
                          href={item.href}
                          naoLidas={naoLidasDoAtendimento}
                        />
                      </Link>
                    );
                  })}
                </nav>
              </div>
            </div>
          </div>
        )}

        {/* Main content — scrolls independentemente, header+sidebar ficam fixos */}
        <main className="flex-1 overflow-y-auto overflow-x-hidden">{children}</main>
      </div>

      {/* UI-4b — o convite de notificação, que até aqui só existia na app
          interna (App.tsx, dentro de <ProtectedRoute>). Sem isto o parceiro
          nunca era sequer perguntado, e o push era inalcançável para ele.

          `PUSH_BASE_PORTAL` porque o middleware DEFAULT DENY não o deixa chegar
          em `/push/*`; o backend monta as mesmas rotas sob `/portal`.

          O componente se auto-gate: só aparece a partir da 3ª visita, com
          permissão "default", sem subscription, fora da janela de dismiss — e
          somente se o SERVIDOR tiver chave VAPID. Esta última é a que impede o
          pior caso: pedir permissão e nunca conseguir enviar. */}
      <NotificationPrompt basePath={PUSH_BASE_PORTAL} />
    </div>
  );
};

export default PortalLayout;
